import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { internalDiscussionsService } from "../services/internalDiscussions.service";
import type {
  CreateDiscussionRequest,
  ResolveDiscussionRequest,
  CancelDiscussionRequest,
  AddCommentRequest,
  UpdateCommentRequest,
  CaseComment,
} from "../services/internalDiscussions.service";

// Context type for optimistic updates
interface AddCommentContext {
  previousComments: CaseComment[] | undefined;
  tempId: string;
}

// ==================== DISCUSSION HOOKS ====================

/**
 * Get all unresolved discussions (discussion queue)
 */
export const useDiscussionQueue = () => {
  return useQuery({
    queryKey: ["internal-discussions", "queue"],
    queryFn: () => internalDiscussionsService.getQueue(),
  });
};

/**
 * Get resolved discussions (for Discussion Queue history)
 */
export const useResolvedDiscussionQueue = (limit?: number) => {
  return useQuery({
    queryKey: ["internal-discussions", "resolved", limit],
    queryFn: () => internalDiscussionsService.getResolvedQueue(limit),
  });
};

/**
 * Get discussions assigned to current user
 */
export const useMyAssignments = () => {
  return useQuery({
    queryKey: ["internal-discussions", "my-assignments"],
    queryFn: () => internalDiscussionsService.getMyAssignments(),
  });
};

/**
 * Get discussions for a specific case
 * Polls every 15 seconds for near-real-time updates on discussion status
 */
export const useCaseDiscussions = (caseId: string) => {
  return useQuery({
    queryKey: ["internal-discussions", "case", caseId],
    queryFn: () => internalDiscussionsService.getByCaseId(caseId),
    enabled: !!caseId,
    refetchInterval: 15000, // Poll every 15 seconds for discussion status changes
  });
};

/**
 * Get discussions assigned to a specific user
 */
export const useAssignedDiscussions = (userId: string) => {
  return useQuery({
    queryKey: ["internal-discussions", "assigned", userId],
    queryFn: () => internalDiscussionsService.getAssignedTo(userId),
    enabled: !!userId,
  });
};

/**
 * Create a new internal discussion
 */
export const useCreateDiscussion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDiscussionRequest) =>
      internalDiscussionsService.createDiscussion(data),
    onSuccess: (_, variables) => {
      // Invalidate queue and case discussions
      queryClient.invalidateQueries({
        queryKey: ["internal-discussions", "queue"],
      });
      queryClient.invalidateQueries({
        queryKey: ["internal-discussions", "case", variables.caseId],
      });
      if (variables.assignedToId) {
        queryClient.invalidateQueries({
          queryKey: [
            "internal-discussions",
            "assigned",
            variables.assignedToId,
          ],
        });
      }
    },
  });
};

/**
 * Resolve a discussion
 */
export const useResolveDiscussion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      discussionId,
      data,
    }: {
      discussionId: string;
      data: ResolveDiscussionRequest;
    }) => internalDiscussionsService.resolveDiscussion(discussionId, data),
    onSuccess: () => {
      // Invalidate all discussion queries
      queryClient.invalidateQueries({ queryKey: ["internal-discussions"] });
    },
  });
};

/**
 * Cancel a discussion (per C-813 spec Section 6)
 */
export const useCancelDiscussion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      discussionId,
      data,
    }: {
      discussionId: string;
      data: CancelDiscussionRequest;
    }) => internalDiscussionsService.cancelDiscussion(discussionId, data),
    onSuccess: () => {
      // Invalidate all discussion queries
      queryClient.invalidateQueries({ queryKey: ["internal-discussions"] });
    },
  });
};

/**
 * Get count of pending discussions (for stats)
 */
export const usePendingDiscussionCount = () => {
  return useQuery({
    queryKey: ["internal-discussions", "count", "pending"],
    queryFn: () => internalDiscussionsService.getPendingCount(),
  });
};

// ==================== COMMENT HOOKS ====================

/**
 * Get comments for a case
 * Polls every 5 seconds for near-real-time chat updates
 */
export const useCaseComments = (caseId: string) => {
  return useQuery({
    queryKey: ["case-comments", "case", caseId],
    queryFn: () => internalDiscussionsService.getCaseComments(caseId),
    enabled: !!caseId,
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  });
};

/**
 * Get comments for a discussion
 */
export const useDiscussionComments = (discussionId: string) => {
  return useQuery({
    queryKey: ["case-comments", "discussion", discussionId],
    queryFn: () =>
      internalDiscussionsService.getDiscussionComments(discussionId),
    enabled: !!discussionId,
  });
};

/**
 * Add a comment with optimistic updates for real-time chat experience
 */
export const useAddComment = (currentUser?: {
  id: string;
  fullName: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation<CaseComment, Error, AddCommentRequest, AddCommentContext>({
    mutationFn: (data: AddCommentRequest) =>
      internalDiscussionsService.addComment(data),
    onMutate: async (newComment) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: ["case-comments", "case", newComment.caseId],
      });

      // Snapshot the previous value
      const previousComments = queryClient.getQueryData<CaseComment[]>([
        "case-comments",
        "case",
        newComment.caseId,
      ]);

      // Generate a temporary ID for the optimistic comment
      const tempId = `temp-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;

      // Create optimistic comment
      const optimisticComment: CaseComment = {
        id: tempId,
        caseId: newComment.caseId,
        userId: currentUser?.id || "",
        content: newComment.content,
        internalDiscussionId: newComment.discussionId || null,
        parentCommentId: newComment.parentCommentId || null,
        isInternal: newComment.isInternal ?? true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        user: currentUser
          ? { id: currentUser.id, fullName: currentUser.fullName }
          : undefined,
        optimisticStatus: "sending",
        optimisticTempId: tempId,
      };

      // Optimistically update the cache
      queryClient.setQueryData<CaseComment[]>(
        ["case-comments", "case", newComment.caseId],
        (old) => [...(old || []), optimisticComment]
      );

      // Return context with the snapshotted value
      return { previousComments, tempId };
    },
    onSuccess: (serverComment, variables, context) => {
      // Replace the optimistic comment with the real one from the server
      queryClient.setQueryData<CaseComment[]>(
        ["case-comments", "case", variables.caseId],
        (old) =>
          old?.map((comment) =>
            comment.optimisticTempId === context?.tempId
              ? { ...serverComment, optimisticStatus: "sent" as const }
              : comment
          ) || []
      );

      // If linked to a discussion, invalidate discussion comments
      if (variables.discussionId) {
        queryClient.invalidateQueries({
          queryKey: ["case-comments", "discussion", variables.discussionId],
        });
      }
    },
    onError: (_err, variables, context) => {
      // Mark the optimistic comment as failed (don't remove it, so user can retry)
      queryClient.setQueryData<CaseComment[]>(
        ["case-comments", "case", variables.caseId],
        (old) =>
          old?.map((comment) =>
            comment.optimisticTempId === context?.tempId
              ? { ...comment, optimisticStatus: "error" as const }
              : comment
          ) || []
      );
    },
  });
};

/**
 * Remove a failed optimistic comment from the cache
 */
export const useRemoveFailedComment = () => {
  const queryClient = useQueryClient();

  return (caseId: string, tempId: string) => {
    queryClient.setQueryData<CaseComment[]>(
      ["case-comments", "case", caseId],
      (old) =>
        old?.filter((comment) => comment.optimisticTempId !== tempId) || []
    );
  };
};

/**
 * Update a comment
 */
export const useUpdateComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      commentId,
      data,
    }: {
      commentId: string;
      data: UpdateCommentRequest;
    }) => internalDiscussionsService.updateComment(commentId, data),
    onSuccess: () => {
      // Invalidate all comment queries
      queryClient.invalidateQueries({ queryKey: ["case-comments"] });
    },
  });
};

/**
 * Delete a comment
 */
export const useDeleteComment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: string) =>
      internalDiscussionsService.deleteComment(commentId),
    onSuccess: () => {
      // Invalidate all comment queries
      queryClient.invalidateQueries({ queryKey: ["case-comments"] });
    },
  });
};
